import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import classNames from 'classnames';
import { z } from 'zod';

import { useUserBalancesQuery } from '@query';

import { UserBalances } from '@api';

import {
  REWARD_POOL_TICKET_CURRENCY,
  RewardPool,
  RewardPoolTicketCurrency,
  RewardPoolTicketCurrencyType,
} from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { useRewardPoolSelfEntries } from '@shared/hooks/query/rewardPools';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  calculateMaxAvailableTickets,
  checkIsInsufficientBalance,
  useBuyEntry,
} from '@store:web/components/entities/rewardPool';
import {
  DialogButton,
  DialogButtonsWrapper,
  DialogCancelButton,
} from '@ui/uikit/components/common/Dialog';
import { FormInput } from '@ui/uikit/components/common/FormInput';
import { Minus, Plus } from '@ui/uikit/components/icons/mono';
import { formatReward } from '@ui/uikit/utils';

import { DeductionHint } from './DeductionHint';
import { PresetButtons } from './PresetButtons';
import { PriceDetails } from './PriceDetails';

const renderHintMessage = (
  currency: RewardPoolTicketCurrency,
  balance: number,
  errorMessage?: string,
) => {
  const message =
    currency === REWARD_POOL_TICKET_CURRENCY.FREE
      ? `Raffle Credits Balance: ${balance}`
      : `Magic ID ${currency} Balance: ${formatReward(balance)}`;

  return (
    <div>
      <div>{message}</div>
      {Boolean(errorMessage) && <div>{errorMessage}</div>}
    </div>
  );
};

const BalanceToTicketCurrencyMap: Partial<Record<RewardPoolTicketCurrency, keyof UserBalances>> = {
  [REWARD_POOL_TICKET_CURRENCY.SQR]: 'sqr',
  [REWARD_POOL_TICKET_CURRENCY.SQRp]: 'sqrp',
  [REWARD_POOL_TICKET_CURRENCY.FREE]: 'raffleTickets',
  [REWARD_POOL_TICKET_CURRENCY.USDT]: 'usdt',
};

const MIN_TICKETS_AMOUNT = 1;

const formatNumberInput = (value: string, maxLimit: number): string => {
  if (Number(value) < MIN_TICKETS_AMOUNT && value !== '') {
    return String(MIN_TICKETS_AMOUNT);
  }

  if (Number(value) > maxLimit) {
    return String(Math.max(maxLimit, MIN_TICKETS_AMOUNT));
  }

  return value.replace(/\D/g, '');
};

type Props = {
  className?: string;
  currencyType: RewardPoolTicketCurrencyType;
  onSubmit: () => void;
  rewardPool: RewardPool;
};

const SHOW_DEPOSIT_FOR_CURRENCIES = [
  REWARD_POOL_TICKET_CURRENCY.SQR,
  REWARD_POOL_TICKET_CURRENCY.USDT,
];

type CheckIsShowDepositButtonProps = {
  currencyType: RewardPoolTicketCurrencyType;
  value: string;
  balance: number;
};

const checkIsShowDepositButton = ({
  currencyType,
  value,
  balance,
}: CheckIsShowDepositButtonProps) => {
  const isInsufficientBalance = checkIsInsufficientBalance({
    price: currencyType.price,
    balance,
    entryAmount: value,
  });

  return (
    isInsufficientBalance &&
    SHOW_DEPOSIT_FOR_CURRENCIES.some((currency) => currencyType.currency === currency)
  );
};

export const GetTicketsForm = ({ className, currencyType, onSubmit, rewardPool }: Props) => {
  const { user } = useAuth();

  const { data: balances } = useUserBalancesQuery(user?.id, { enabled: Boolean(user?.id) });
  const { data: entries = [] } = useRewardPoolSelfEntries(rewardPool.id);

  const { mutateAsync: buyEntry } = useBuyEntry({ rewardPool });
  const { currency, price } = currencyType;

  const key = BalanceToTicketCurrencyMap[currency];
  const balance = key && balances && balances[key] ? balances[key] : 0;

  const FormSchema = useMemo(() => {
    return z.object({
      amount: z
        .string()
        .min(1, { message: 'Field is required.' })
        .transform((value) => parseInt(value, 10))
        .refine((value) => value * price <= balance, { message: 'Insufficient balance.' })
        .transform((value) => value.toString()),
    });
  }, [price, balance]);

  const [defaultValues] = useState(() => {
    return { amount: '' };
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
  });
  const { handleSubmit, control, formState, reset, watch, trigger } = form;
  const watchedAmount = watch('amount');
  const [showDepositButton, setShowDeposit] = useState(false);

  useEffect(() => {
    const show = checkIsShowDepositButton({
      currencyType,
      balance,
      value: watchedAmount,
    });

    if (watchedAmount) {
      void trigger('amount');
    }

    setShowDeposit(show);
  }, [watchedAmount]);

  const submit = handleSubmit(async (data) => {
    try {
      await buyEntry({ currencyType, entryAmount: Number(data.amount) });
      reset(defaultValues);
      onSubmit();
    } catch (error) {
      displayErrorToast({ error });
    }
  });

  const { isDirty, errors, isSubmitting } = formState;
  const hasErrors = Object.keys(errors).length > 0;
  const isSubmitButtonDisabled = !isDirty || hasErrors || isSubmitting;

  return (
    <form onSubmit={submit} className={classNames('flex flex-col', className)}>
      <Controller
        control={control}
        name="amount"
        render={({ field: { ref, onChange, value, ...field }, fieldState }) => {
          const maxAvailableTickets = calculateMaxAvailableTickets({
            balance,
            price: currencyType.price,
            entryAmount: entries.length,
            rewardPool,
          });

          const handleChange = (v: string) => {
            onChange(formatNumberInput(v, maxAvailableTickets));
          };

          const handleIncrement = (increment: number) => () => {
            const newValue = String(Number(value) + increment);
            handleChange(newValue);
          };

          return (
            <div className="flex flex-col gap-6 py-6">
              <div className="flex flex-col gap-3">
                <FormInput
                  inputRef={ref}
                  onValueChange={handleChange}
                  value={value}
                  label="Number of tickets"
                  required={true}
                  data-qa="profile-display-name-input"
                  isError={!!fieldState.error}
                  hint={renderHintMessage(currency, balance, fieldState.error?.message)}
                  afterInput={
                    <div className="flex gap-2">
                      <button
                        type="button"
                        data-skeleton-target="base"
                        value={value}
                        className="msq-btn msq-btn-secondary msq-btn-icon-lg"
                        onClick={handleIncrement(-1)}
                      >
                        <Minus className="size-4" />
                      </button>
                      <button
                        type="button"
                        data-skeleton-target="base"
                        value={value}
                        className="msq-btn msq-btn-secondary msq-btn-icon-lg"
                        onClick={handleIncrement(1)}
                      >
                        <Plus className="size-4" />
                      </button>
                    </div>
                  }
                  {...field}
                />
                {currency !== REWARD_POOL_TICKET_CURRENCY.FREE && (
                  <PresetButtons
                    price={currencyType.price}
                    balance={balance}
                    onChange={handleChange}
                    rewardPool={rewardPool}
                    entryAmount={entries.length}
                  />
                )}
              </div>

              <PriceDetails currencyType={currencyType} price={2} ticketsAmount={Number(value)} />

              <DeductionHint currency={currency} />
            </div>
          );
        }}
      />
      <DialogButtonsWrapper>
        <DialogCancelButton />
        {showDepositButton ? (
          <DialogButton href="/profile/membership/dashboard" target="_blank">
            Deposit {currencyType.currency}
          </DialogButton>
        ) : (
          <DialogButton type="submit" isDisabled={isSubmitButtonDisabled}>
            Confirm
          </DialogButton>
        )}
      </DialogButtonsWrapper>
    </form>
  );
};
